import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Pagination,
  useMediaQuery,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideocamIcon from "@mui/icons-material/Videocam";
import ImageIcon from "@mui/icons-material/Image";
import axios from "axios";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Tooltip from "@mui/material/Tooltip";

const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case "completed":
      return { bg: "#e8f5e9", text: "#2e7d32", border: "#c8e6c9" };
    case "pending":
      return { bg: "#fff8e1", text: "#f57f17", border: "#ffecb3" };
    case "inprogress":
      return { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" };
    case "cancelled":
      return { bg: "#ffebee", text: "#c62828", border: "#ffcdd2" };
    case "reinitiated":
      return { bg: "#f3e5f5", text: "#7b1fa2", border: "#e1bee7" };
    case "failed":
      return { bg: "#fbe9e7", text: "#d84315", border: "#ffab91" };
    case "rejected":
      return { bg: "#eceff1", text: "#37474f", border: "#cfd8dc" };
    default:
      return { bg: "#f5f5f5", text: "#424242", border: "#e0e0e0" };
  }
};

const StatusChip = ({ status }) => {
  const colors = getStatusColor(status);
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        fontWeight: "bold",
        fontSize: "0.75rem",
        height: "24px",
        minWidth: "80px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// Tab Panel Component for Media Tabs
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`media-tabpanel-${index}`}
      aria-labelledby={`media-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
};

// Function to decode and fix backend message encoding issues
const decodeBackendMessage = (message) => {
  if (!message) return "N/A";

  // Common encoding fixes for backend responses
  const decodedMessage = message
    // Fix smart quotes and apostrophes
    .replace(/â/g, "'")
    .replace(/â/g, "'")
    .replace(/â/g, '"')
    .replace(/â/g, '"')
    // Fix bullet points
    .replace(/â¢/g, "•")
    // Fix rupee symbol
    .replace(/â¹/g, "₹")
    // Fix em dash
    .replace(/â/g, "—")
    .replace(/â/g, "–")
    // Fix pointing hand emoji and other common emojis
    .replace(/ð/g, "👉")
    .replace(/ð/g, "🎉")
    .replace(/ð/g, "😊")
    .replace(/ð/g, "😃")
    .replace(/ð/g, "🚀")
    .replace(/ð/g, "📑")
    .replace(/ð/g, "📎")
    .replace(/ð/g, "📌")
    .replace(/â¨/g, "✨")
    .replace(/ð´/g, "🔄")
    .replace(/ð»/g, "🛫")
    .replace(/ð½/g, "🛬")
    .replace(/ð/g, "🚁")
    .replace(/ð/g, "🚌")
    .replace(/ð/g, "🔄")
    .replace(/ð/g, "🔅")
    .replace(/ð/g, "🔆")
    .replace(/ð/g, "🔇")
    .replace(/ð/g, "🔈")
    .replace(/ð/g, "🔉")
    .replace(/ð/g, "🔊")
    .replace(/ð/g, "🔋")
    .replace(/ð/g, "🔌")
    .replace(/ð/g, "🔍")
    .replace(/ð/g, "🔎")
    .replace(/ð/g, "🔏")
    .replace(/ð/g, "🔐")
    .replace(/ð/g, "🔑")
    .replace(/ð/g, "🔒")
    .replace(/ð/g, "🔓")
    .replace(/ð/g, "🔔")
    .replace(/ð/g, "🔕")
    .replace(/ð/g, "🔖")
    .replace(/ð/g, "🔗")
    .replace(/ð/g, "🔘")
    .replace(/ð/g, "🔙")
    .replace(/ð/g, "🔚")
    .replace(/ð/g, "🔛")
    .replace(/ð/g, "🔜")
    .replace(/ð/g, "🔝")
    .replace(/ð/g, "🔞")
    .replace(/ð/g, "🔟")
    // Fix euro and other currency symbols
    .replace(/â¬/g, "€")
    .replace(/Â£/g, "£")
    .replace(/Â¥/g, "¥")
    // Fix common special characters
    .replace(/Â°/g, "°")
    .replace(/Â±/g, "±")
    .replace(/Â²/g, "²")
    .replace(/Â³/g, "³")
    .replace(/Âµ/g, "µ")
    .replace(/Â·/g, "·")
    .replace(/Â¼/g, "¼")
    .replace(/Â½/g, "½")
    .replace(/Â¾/g, "¾")
    .replace(/Â¿/g, "¿")
    .replace(/Â¡/g, "¡")
    .replace(/Â¢/g, "¢")
    .replace(/Â¤/g, "¤")
    .replace(/Â¦/g, "¦")
    .replace(/Â§/g, "§")
    .replace(/Â¨/g, "¨")
    .replace(/Â©/g, "©")
    .replace(/Âª/g, "ª")
    .replace(/Â«/g, "«")
    .replace(/Â»/g, "»")
    .replace(/Â¬/g, "¬")
    .replace(/Â®/g, "®")
    .replace(/Â¯/g, "¯")
    .replace(/Â´/g, "´")
    .replace(/Â¶/g, "¶")
    .replace(/Â¸/g, "¸")
    .replace(/Âº/g, "º")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "")
    .replace(/Â/g, "");

  return decodedMessage;
};

const CampaignTable = () => {
  const [page, setPage] = useState(1);
  const [searchKey, setSearchKey] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "none",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [updating, setUpdating] = useState(false);
  const [mediaTabValue, setMediaTabValue] = useState(0);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");

  // ✅ Helper to filter only Reinitiated, Inprogress, Pending
  const filterActiveStatuses = (data) =>
    data.filter((item) =>
      ["reinitiated", "inprogress", "pending"].includes(
        item.status?.toLowerCase()
      )
    );

  // ✅ Fetch all campaigns
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/latest_camp"
      );
      const items = res?.data?.["body-json"]?.body?.Items || [];

      const mapped = items.map((item) => ({
        userId: item.persion_name || "-",
        campaignId: item.id || "-",
        status:
          item.cmp_status?.charAt(0).toUpperCase() +
            item.cmp_status?.slice(1) || "-",
        date: item.creationdate || "-",
        time: item.time || "-",
        csvUrl: item.csv || "",
        remarks: item.remarks || "",
        username: item.username || "",
        imageUrl: item.image_url?.[0] || "",
        message: decodeBackendMessage(item.message) || "",
        videoUrl: item.video_url || "",
        pdfUrl: item.pdf_url || "",
        imageUrls: item.image_url || [],
      }));

      const filtered = filterActiveStatuses(mapped);
      setCampaigns(filtered);
    } catch (error) {
      console.error("API Error:", error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // ✅ Search campaigns
  const fetchSearchResults = async (key) => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/campaign_search",
        { params: { key } }
      );

      const items =
        res?.data?.body?.Items || res?.data?.["body-json"]?.body?.Items || [];

      const mapped = items.map((item) => ({
        UserID: item.username || "-",
        campaignId: item.id || "-",
        status:
          item.cmp_status?.charAt(0).toUpperCase() +
            item.cmp_status?.slice(1) || "-",
        date: item.creationdate || "-",
        time: item.time || "-",
        csvUrl: item.csv || "",
        remarks: item.remarks || "",
        username: item.username || "",
        imageUrl: item.image_url?.[0] || "",
        message: decodeBackendMessage(item.message) || "",
        videoUrl: item.video_url || "",
        pdfUrl: item.pdf_url || "",
        imageUrls: item.image_url || [],
      }));

      const filtered = filterActiveStatuses(mapped);
      setFilteredData(filtered);
      setPage(1);
    } catch (error) {
      console.error("Search error:", error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      if (searchKey.trim()) fetchSearchResults(searchKey.trim());
      else {
        setFilteredData([]);
        fetchCampaigns();
      }
    }
  };

  const handleClearSearch = () => {
    setSearchKey("");
    setFilteredData([]);
    fetchCampaigns();
  };

  const handlePageChange = (_, value) => setPage(value);

  // ✅ Sorting logic
  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = "none";
      }
    }

    setSortConfig({ key, direction });

    if (direction === "none") {
      const dataToSort = filteredData.length
        ? [...filteredData]
        : [...campaigns];
      setFilteredData([]);
      setCampaigns(dataToSort);
      return;
    }

    const dataToSort = filteredData.length ? [...filteredData] : [...campaigns];

    const sortedData = [...dataToSort].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      if (key === "username" || key === "campaignId" || key === "status") {
        const valA = String(aValue || "")
          .toLowerCase()
          .trim();
        const valB = String(bValue || "")
          .toLowerCase()
          .trim();
        return direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if (key === "date") {
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          const parts = dateStr.toString().split("/");
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            if (
              day >= 1 &&
              day <= 31 &&
              month >= 0 &&
              month <= 11 &&
              year > 0
            ) {
              return new Date(year, month, day);
            }
          }
          const date = dayjs(dateStr, [
            "DD/MM/YYYY",
            "MM/DD/YYYY",
            "YYYY-MM-DD",
          ]);
          return date.isValid() ? date.toDate() : null;
        };

        const dateA = parseDate(aValue);
        const dateB = parseDate(bValue);

        if (!dateA && !dateB) return 0;
        if (!dateA) return direction === "asc" ? 1 : -1;
        if (!dateB) return direction === "asc" ? -1 : 1;

        return direction === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      if (key === "time") {
        let timeA = dayjs(aValue, "HH:mm:ss");
        let timeB = dayjs(bValue, "HH:mm:ss");

        if (!timeA.isValid()) timeA = dayjs(aValue, "HH:mm");
        if (!timeB.isValid()) timeB = dayjs(bValue, "HH:mm");

        if (!timeA.isValid() || !timeB.isValid()) {
          return direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }

        return direction === "asc"
          ? timeA.valueOf() - timeB.valueOf()
          : timeB.valueOf() - timeA.valueOf();
      }

      return direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    if (filteredData.length) {
      setFilteredData(sortedData);
    } else {
      setCampaigns(sortedData);
    }
    setPage(1);
  };

  // Get sort icon for a column
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    }

    switch (sortConfig.direction) {
      case "asc":
        return <ArrowUpwardIcon fontSize="small" color="primary" />;
      case "desc":
        return <ArrowDownwardIcon fontSize="small" color="primary" />;
      default:
        return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    }
  };

  // ✅ Edit logic
  const handleEditClick = (row) => {
    setSelectedRow(row);
    setStatus(row.status.toLowerCase());
    setRemarks(row.remarks || "");
    setCsvFile(null);
    setCsvPreviewData([]);
    setOpenDialog(true);
  };

  const handleViewClick = (row) => {
    setSelectedRow(row);
    setMediaTabValue(0);
    setOpenViewDialog(true);
  };

  // Handle media tab change
  const handleMediaTabChange = (event, newValue) => {
    setMediaTabValue(newValue);
  };

  // ✅ Handle file upload and parse CSV for preview
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["text/csv", "application/vnd.ms-excel"];
      const isValidType =
        validTypes.includes(file.type) || file.name.endsWith(".csv");

      if (!isValidType) {
        setSnackbar({
          open: true,
          message: "Please upload a valid CSV file",
          severity: "error",
        });
        return;
      }

      setCsvFile(file);

      // Parse CSV for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const lines = csvText
            .split("\n")
            .filter((line) => line.trim() !== "");

          if (lines.length === 0) {
            setCsvPreviewData([]);
            return;
          }

          // Parse headers
          const headers = lines[0].split(",").map((header) => header.trim());

          // Parse data rows (limit to first 5 rows for preview)
          const previewRows = [];
          for (let i = 1; i < Math.min(lines.length, 6); i++) {
            const values = lines[i].split(",").map((value) => value.trim());
            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index] || "";
            });
            previewRows.push(rowData);
          }

          setCsvPreviewData(previewRows);
        } catch (error) {
          console.error("Error parsing CSV:", error);
          setSnackbar({
            open: true,
            message: "Error parsing CSV file",
            severity: "error",
          });
          setCsvPreviewData([]);
        }
      };
      reader.onerror = () => {
        setSnackbar({
          open: true,
          message: "Error reading CSV file",
          severity: "error",
        });
      };
      reader.readAsText(file);
    }
  };

  // ✅ Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // ✅ Handle status update with CSV data - UPDATED PAYLOAD STRUCTURE
  const handleStatusUpdate = async () => {
    if (!selectedRow) return;

    // Validate that CSV is uploaded when status is completed
    if (status === "completed" && !csvFile) {
      setSnackbar({
        open: true,
        message: "Please upload a CSV file for completed status",
        severity: "warning",
      });
      return;
    }

    setUpdating(true);
    try {
      let csvData = null;

      // Process CSV file if status is completed and file is selected
      if (status === "completed" && csvFile) {
        setSnackbar({
          open: true,
          message: "Processing CSV file...",
          severity: "info",
        });

        // Convert CSV to base64
        const base64Data = await fileToBase64(csvFile);

        // ✅ Create the exact CSV data structure as in your example
        csvData = {
          name: csvFile.name,
          base64: base64Data,
        };
      }

      // ✅ Create the exact payload structure as in your example
      const payload = {
        id: selectedRow.campaignId,
        cmp_status: status,
        remarks: remarks || "",
        reinitiated: 0,
        ...(status === "completed" && csvData && { csvData: csvData }),
      };

      console.log("Sending payload:", JSON.stringify(payload, null, 2));

      const updatedStatus = status.charAt(0).toUpperCase() + status.slice(1);

      // Update campaign status
      const response = await axios.put(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/status_update",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      console.log("Update response:", response.data);

      setSnackbar({
        open: true,
        message: `Status updated to ${updatedStatus}${
          status === "completed" ? " with CSV file" : ""
        }`,
        severity: "success",
      });

      setOpenDialog(false);
      setCsvFile(null);
      setCsvPreviewData([]);
      fetchCampaigns();
    } catch (error) {
      console.error("Update Error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update status.",
        severity: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Download CSV directly
  const handleDownload = async (row) => {
    try {
      if (!row.csvUrl) {
        setSnackbar({
          open: true,
          message: "No CSV data available for download",
          severity: "warning",
        });
        return;
      }

      setSnackbar({
        open: true,
        message: "Downloading CSV file...",
        severity: "info",
      });

      // Fetch CSV data from the URL
      const response = await fetch(row.csvUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status}`);
      }

      const csvText = await response.text();

      // Create blob and download
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const campaignId = row.campaignId || "campaign";
      const fileName = `campaign_${campaignId}_${dayjs().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.csv`;

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "CSV file downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setSnackbar({
        open: true,
        message: "Failed to download CSV file: " + error.message,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Reset form when dialog closes
  const handleDialogClose = () => {
    setOpenDialog(false);
    setCsvFile(null);
    setCsvPreviewData([]);
  };

  const displayedData = filteredData.length ? filteredData : campaigns;
  const paginatedData = displayedData.slice((page - 1) * 10, page * 10);

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fc", minHeight: "100vh", mt: 2 }}>
      {/* Search */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search by Name, Username, Email"
          variant="outlined"
          size="small"
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          onKeyDown={handleSearchSubmit}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchKey && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 3, backgroundColor: "white" },
          }}
          sx={{ width: isMobile ? "100%" : "50%" }}
        />
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography>Loading campaigns...</Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#e0f7fa" }}>
            <TableRow>
              <TableCell
                onClick={() => handleSort("username")}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { backgroundColor: "#d4f1f4" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <b>USER ID</b>
                  {getSortIcon("username")}
                </Box>
              </TableCell>

              <TableCell
                onClick={() => handleSort("campaignId")}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { backgroundColor: "#d4f1f4" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <b>CAMPAIGN ID</b>
                  {getSortIcon("campaignId")}
                </Box>
              </TableCell>

              <TableCell
                onClick={() => handleSort("status")}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { backgroundColor: "#d4f1f4" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <b>STATUS</b>
                  {getSortIcon("status")}
                </Box>
              </TableCell>

              <TableCell
                onClick={() => handleSort("date")}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { backgroundColor: "#d4f1f4" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <b>DATE</b>
                  {getSortIcon("date")}
                </Box>
              </TableCell>

              <TableCell>
                <b>ACTION</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, i) => (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "white" : "#f0f6ff",
                    "&:hover": { backgroundColor: "#e3f2fd" },
                  }}
                >
                  <TableCell>{row.username}</TableCell>
                  <TableCell>{row.campaignId}</TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.date}, {row.time}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "#1976d2", color: "#fff", p: "4px" }}
                          onClick={() => handleViewClick(row)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status">
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "#ed6c02", color: "#fff", p: "4px" }}
                          onClick={() => handleEditClick(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download CSV">
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "#2e7d32", color: "#fff", p: "4px" }}
                          onClick={() => handleDownload(row)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No campaigns found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {displayedData.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Pagination
            count={Math.ceil(displayedData.length / 10)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="primary"
          />
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f5f5", borderBottom: 1, borderColor: "divider" }}
        >
          Edit Campaign Status
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            fullWidth
          >
            {[
              "inprogress",
              "completed",
              "cancelled",
              "reinitiated",
              "failed",
              "pending",
            ].map((option) => (
              <MenuItem key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </MenuItem>
            ))}
          </TextField>

          {status === "completed" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                Upload Report CSV File *
              </Typography>

              {/* File Upload Button */}
              <Button
                variant="outlined"
                component="label"
                fullWidth
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  border: "2px dashed #1976d2",
                  backgroundColor: csvFile
                    ? "rgba(25, 118, 210, 0.04)"
                    : "rgba(25, 118, 210, 0.02)",
                  color: "#1976d2",
                  padding: "12px 16px",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    border: "2px dashed #1565c0",
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                  },
                }}
              >
                {csvFile ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: "success.main",
                        fontSize: "1.2rem",
                        mr: 1,
                      }}
                    />
                    <Box sx={{ flex: 1, textAlign: "left" }}>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        {csvFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "success.main" }}
                      >
                        File selected successfully - Click to change
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <CloudUploadIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    <Box sx={{ flex: 1, textAlign: "left" }}>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        Choose CSV File
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Click to upload campaign report CSV
                      </Typography>
                    </Box>
                  </Box>
                )}
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>

              {/* CSV Preview */}
              {csvPreviewData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    CSV Preview (First {csvPreviewData.length} rows):
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 200, overflow: "auto" }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {Object.keys(csvPreviewData[0] || {}).map(
                            (header) => (
                              <TableCell
                                key={header}
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "#f5f5f5",
                                  position: "sticky",
                                  top: 0,
                                }}
                              >
                                {header}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvPreviewData.map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "#fafafa",
                              },
                            }}
                          >
                            {Object.values(row).map((value, cellIndex) => (
                              <TableCell key={cellIndex}>
                                {String(value)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Sample CSV Download */}
              <Box
                sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  * CSV file is required when marking campaign as completed
                </Typography>

                <Tooltip title="Download Sample Report CSV File">
                  <Button
                    component="a"
                    href="/sample_report.csv"
                    download="sample_report.csv"
                    startIcon={<DownloadIcon />}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      borderRadius: 1,
                      alignSelf: "flex-start",
                    }}
                  >
                    Download Sample CSV
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          )}

          <TextField
            label="Remarks"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            size="small"
            fullWidth
            placeholder="Add any remarks or notes about this campaign status change..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            size="medium"
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            size="medium"
            disabled={updating}
            sx={{ minWidth: 120 }}
          >
            {updating ? "Updating..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f5f5", borderBottom: 1, borderColor: "divider" }}
        >
          Campaign Details
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: "70vh",
            overflow: "auto",
          }}
        >
          {selectedRow && (
            <>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Typography>
                  <b>User ID:</b> {selectedRow.username}
                </Typography>
                <Typography>
                  <b>Campaign ID:</b> {selectedRow.campaignId}
                </Typography>
                <Typography>
                  <b>Status:</b> <StatusChip status={selectedRow.status} />
                </Typography>
                <Typography>
                  <b>Date & Time:</b> {selectedRow.date}, {selectedRow.time}
                </Typography>
              </Box>

              <Typography>
                <b>Remarks:</b> {selectedRow.remarks || "N/A"}
              </Typography>

              <Box>
                <Typography sx={{ mb: 1 }}>
                  <b>Message:</b>
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    maxHeight: "200px",
                    overflow: "auto",
                    fontSize: "0.9rem",
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    fontFamily: "inherit",
                  }}
                >
                  {selectedRow.message || "N/A"}
                </Box>
              </Box>

              {/* Media Tabs Section */}
              {(selectedRow.imageUrls?.length > 0 ||
                selectedRow.videoUrl ||
                selectedRow.pdfUrl) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Media Files
                  </Typography>

                  <Tabs
                    value={mediaTabValue}
                    onChange={handleMediaTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {selectedRow.imageUrls?.length > 0 && (
                      <Tab
                        icon={<ImageIcon />}
                        label={`Images (${selectedRow.imageUrls.length})`}
                      />
                    )}
                    {selectedRow.videoUrl && (
                      <Tab icon={<VideocamIcon />} label="Video" />
                    )}
                    {selectedRow.pdfUrl && (
                      <Tab icon={<PictureAsPdfIcon />} label="PDF" />
                    )}
                  </Tabs>

                  {/* Images Tab */}
                  {selectedRow.imageUrls?.length > 0 && (
                    <TabPanel value={mediaTabValue} index={0}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {selectedRow.imageUrls.map((imgUrl, index) => (
                          <Box key={index} sx={{ textAlign: "center" }}>
                            <img
                              src={imgUrl}
                              alt={`Campaign ${index + 1}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: 200,
                                borderRadius: 8,
                                border: "1px solid #ddd",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <Typography variant="caption" display="block">
                              Image {index + 1}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </TabPanel>
                  )}

                  {/* Video Tab */}
                  {selectedRow.videoUrl && (
                    <TabPanel
                      value={mediaTabValue}
                      index={selectedRow.imageUrls?.length > 0 ? 1 : 0}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <video
                          controls
                          style={{
                            maxWidth: "100%",
                            maxHeight: 300,
                            borderRadius: 8,
                          }}
                        >
                          <source src={selectedRow.videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          <a
                            href={selectedRow.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            Open video in new tab
                          </a>
                        </Typography>
                      </Box>
                    </TabPanel>
                  )}

                  {/* PDF Tab */}
                  {selectedRow.pdfUrl && (
                    <TabPanel
                      value={mediaTabValue}
                      index={
                        (selectedRow.imageUrls?.length > 0 ? 1 : 0) +
                        (selectedRow.videoUrl ? 1 : 0)
                      }
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <iframe
                          src={selectedRow.pdfUrl}
                          width="100%"
                          height="400"
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: 8,
                          }}
                          title="PDF Document"
                        >
                          This browser does not support PDFs. Please download
                          the PDF to view it.
                        </iframe>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          <a
                            href={selectedRow.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            Open PDF in new tab
                          </a>
                        </Typography>
                      </Box>
                    </TabPanel>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenViewDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignTable;
